import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { fetchFirebaseFunction } from '@/lib/firebaseFunctions';

export interface CutListEntry {
  partId: string;
  name: string;
  kind: string;
  materialId: string;
  materialName: string;
  widthMm: number;
  heightMm: number;
  thicknessMm: number | null;
  quantity: number;
  cutouts: number;
}

interface Props {
  conversationId: string | undefined;
  checkpointId: string | undefined;
  className?: string;
}

interface ExportResponse {
  ok: boolean;
  dxfBase64?: string;
  cutList?: CutListEntry[];
  skipped?: Array<{ partId: string; reason: string }>;
  error?: string;
}

export function CutListPanel({ conversationId, checkpointId, className }: Props) {
  const { t } = useTranslation('common');
  const [data, setData] = useState<ExportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExport = async () => {
    if (!conversationId || !checkpointId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFirebaseFunction('exportDxf', {
        method: 'POST',
        body: JSON.stringify({ conversationId, checkpointId }),
      });
      const json = (await res.json()) as ExportResponse;
      if (!res.ok || !json.ok) {
        setError(json.error ?? `HTTP ${res.status}`);
        return;
      }
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!data?.dxfBase64) return;
    const bin = atob(data.dxfBase64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/dxf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `design-${checkpointId?.slice(0, 8) ?? 'export'}.dxf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={fetchExport}
          disabled={!conversationId || !checkpointId || loading}
        >
          {loading ? (
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          ) : null}
          {t('drawing.cutListPartsTable')}
        </Button>
        {data?.dxfBase64 && (
          <Button size="sm" onClick={download}>
            <Download className="w-3 h-3 mr-1" />
            DXF
          </Button>
        )}
      </div>
      {error && <div className="text-xs font-bold text-brand-indigo">{error}</div>}
      {data?.cutList && data.cutList.length > 0 && (
        <div className="overflow-x-auto">
          <table className="text-xs w-full">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pr-2">{t('drawing.cutListHeaderNumber')}</th>
                <th className="pr-2">{t('drawing.cutListHeaderPart')}</th>
                <th className="pr-2">{t('drawing.cutListHeaderKind')}</th>
                <th className="pr-2">{t('drawing.cutListHeaderWHT')}</th>
                <th className="pr-2">{t('drawing.cutListHeaderMaterial')}</th>
                <th className="pr-2">{t('drawing.cutListHeaderQty')}</th>
              </tr>
            </thead>
            <tbody>
              {data.cutList.map((row, i) => (
                <tr key={row.partId} className="border-t">
                  <td className="pr-2">{i + 1}</td>
                  <td className="pr-2">{row.name}</td>
                  <td className="pr-2 text-muted-foreground">{row.kind}</td>
                  <td className="pr-2">
                    {row.widthMm > 0
                      ? `${row.widthMm}×${row.heightMm}${row.thicknessMm ? `×${row.thicknessMm}` : ''}`
                      : '—'}
                  </td>
                  <td className="pr-2">{row.materialName}</td>
                  <td className="pr-2">{row.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.skipped && data.skipped.length > 0 && (
        <div className="text-xs text-muted-foreground">
          {t('drawing.cutListFlattenSkipped')} {data.skipped.map((s) => s.partId).join(', ')}
        </div>
      )}
    </div>
  );
}
